/* MUI */
import { Box } from '@mui/material';

/* Objects */
import {
    ApplicationCategoryAndSubCategory,
    ApplicationIcon,
    ApplicationName
} from 'objects/Application';

/* React */
import React from 'react';

export interface ApplicationNameLinkCellProps {
    categoryClassname?: string;
    categoryLines?: number;
    categoryNoTruncate?: boolean;
    categorySize?: 'smaller' | 'small' | 'normal'; // Will be size small is size="normal" is not given
    name?: string;
    nameClassname?: string;
    nameLines?: number; // If not provided it will default to 1 of truncation
    nameNoTruncate?: boolean;
    onNameClick?: () => void;
    scrollFrom?: boolean;
    scrollTo?: boolean;
    showBrowserHistoryBadge?: boolean;
    useWhiteFont?: boolean;
    withCategory?: boolean;
}

/**
 * There are props for two different components ApplicationName and ApplicationCategoryAndSubCategory
 *
 * For ApplicationName we have:
 *      @param {string} [props.name] the name of the app, if we are not getting it from the application.name.
 *      @param {string} [props.nameClassname] to be applied to the name string. If link is set, then it will be overwritten in the component itself to be
 *             "link underline bold".
 *      @param {number} [props.nameLines] to tell TextTruncate to how many lines it will be truncated, if nothing is set, it defaults to 1 line.
 *      @param {boolean} [props.nameNoTruncate] tells it to show the whole text, don't use TextTruncate.
 *      @param {function} [props.onNameClick] gives an action to do when the app name is clicked.
 *      @param {boolean} [props.scrollFrom] and
 *      @param {boolean} [props.scrollTo] work together in printed pdf to reference where to jump back and forth in a report when you click in the app name.
 *
 * For ApplicationCategoryAndSubCategory we have
 *      @param {boolean} [props.withCategory] to show the category, default is false.
 *      @param {string} [props.categoryClassname] to be applied to the name string. If link is set, then it will be overwritten in the component itself to be
 *             "link underline bold".
 *      @param {number} [props.categoryLines] to tell TextTruncate to how many lines it will be truncated, if nothing is set, it defaults to 1 line.
 *      @param {('smaller'|'small'|'normal')} [props.categorySize] not set it will be small, font[0], if regular font size is required, it should be set as categorySize="normal".
 *      @param {boolean} [props.categoryNoTruncate] tells it to show the whole text, don't use TextTruncate.
 *
 *      @returns {JSX.Element} JSX of the icon, clickable app name (that could link to the app's page), and, included, the app's category and subCategory.
 *
 * Note: Always needs to be wrapped, at some higher level by the component `Application` in order to provide the context.
 * @example
 *                 <Application data={application}>
 *                     <ApplicationNameLinkCell withCategory />
 *                 </Application>
 */

export const ApplicationNameLinkCell = ({
    categoryClassname,
    categoryLines,
    categoryNoTruncate,
    categorySize, // Will be size small if size="normal" is not given
    name,
    nameClassname,
    nameLines, // If not provided it will default to 1 of truncation
    nameNoTruncate,
    onNameClick,
    scrollFrom,
    scrollTo,
    showBrowserHistoryBadge,
    useWhiteFont,
    withCategory
}: ApplicationNameLinkCellProps): JSX.Element => (
    <Box
        data-testid="applicationNameLinkCell"
        alignItems="center"
        display="flex"
        width="100%"
    >
        <Box data-testid="appCellNameCategory" width="100%">
            <Box width="100%" display="flex" alignItems="center">
                <Box mr="4px" data-testid="appCellNameIcon" display="flex">
                    <ApplicationIcon sx={{ height: '16px', width: '16px' }} />
                </Box>
                <ApplicationName
                    className={nameClassname}
                    lines={nameLines}
                    name={name}
                    noTruncate={nameNoTruncate}
                    onNameClick={onNameClick}
                    scrollFrom={scrollFrom}
                    scrollTo={scrollTo}
                    showBrowserHistoryBadge={showBrowserHistoryBadge}
                    useWhiteFont={useWhiteFont}
                />
            </Box>
            {withCategory && (
                // 20px = 16 for the icon plus 4 of the icon's margin
                <Box
                    width="100%"
                    pl="20px"
                    data-testid="appCellCatSubCatWrapper"
                >
                    <ApplicationCategoryAndSubCategory
                        className={categoryClassname}
                        lines={categoryLines}
                        noTruncate={categoryNoTruncate}
                        size={categorySize}
                        useWhiteFont={useWhiteFont}
                    />
                </Box>
            )}
        </Box>
    </Box>
);
